/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { chain } from "lodash";
import { reverseStateMap } from "components/haven-design/state-picker";
import { capitalize } from "pages/dashboard/interests-form/interest-form-utils";

type OldFormData = object;
type ParsedFormData = Record<string, boolean | string | null>;

type FormElementLike = {
  type?: string;
  tagName: string;
  value: string;
  name: string;
  checked?: boolean;
}

/** Takes a dumb static form and cleans any values before it's passed
 *  on to graphql mutation, used in new/edit(pm)/edit(client)
 */
export const parseDumbInterestForm = (
  formEl: HTMLFormElement | HTMLElement | EventTarget | null,
  oldFormData?: OldFormData,
) => {
  if (!(formEl instanceof HTMLFormElement)) {
    return {} as ParsedFormData;
  }

  const resultObj = chain(Array.from(formEl.elements) as unknown as FormElementLike[])
    .filter((e) => !!e.name && e.type !== "submit")
    .reduce((obj: ParsedFormData, e: FormElementLike) => {
      if (e.type === "button") {
        return obj; // ignore buttons
      }
      if (e.tagName === "FIELDSET") {
        return obj; // ignore non-input
      }
      // clean any income fields since they might contain non number chars
      if (e.value.match(/^\$/)) {
        e.value = e.value.replace(/\$|,/g, "");
      }
      if (e.type === "checkbox") {
        // unit size checkboxes handled on the be changeset
        if (e.name.startsWith("unitSize")) {
          obj[e.name] = !!e.checked;
          // handle checkbox cases so that when you submit the form,
          // all unchecked boxes won't be set from `null` to `false`
          // which causes noisey diffs on first update
        } else if (oldFormData) {
          const existingValue = (oldFormData as Record<string, boolean | null | undefined>)[e.name];
          if (existingValue === null && e.checked) {
            obj[e.name] = true;
          } else if (existingValue === false && e.checked) {
            obj[e.name] = true;
          } else if (existingValue === true && !e.checked) {
            obj[e.name] = false;
          }
          // we're not using prefilled data so it doesn't matter
        } else {
          obj[e.name] = !!e.checked;
        }
      } else if (e.type === "radio") {
        if (e.checked) {
          if (e.value === "true") {
            obj[e.name] = true;
          } else if (e.value === "false") {
            obj[e.name] = false;
          } else {
            obj[e.name] = e.value.toLowerCase();

            if (e.name === "unitSize" && e.value) {
              obj[`unitSize${capitalize(e.value)}`] = true;
            }
          }
        }
      } else {
        obj[e.name] = e.value;
      }

      return obj;
    }, {} as ParsedFormData)
    .mapValues((val: any) => (typeof val === "undefined" || val === "Select" ? null : val))
    // eslint-disable-next-line complexity
    .mapValues((val: any, key: string) => {
      // if date is empty return null instead of empty string
      switch (key) {
        case "householdMember0Dob":
        case "householdMember1Dob":
        case "householdMember2Dob":
        case "householdMember3Dob":
        case "householdMember4Dob":
        case "householdMember5Dob":
          return val === "" ? null : val;
        case "householdMember0Social":
        case "householdMember1Social":
        case "householdMember2Social":
        case "householdMember3Social":
        case "householdMember4Social":
        case "householdMember5Social":
          return val.replace(/[_-]/g, "");
        case "householdMember0TaxIdNumber":
        case "householdMember1TaxIdNumber":
        case "householdMember2TaxIdNumber":
          return val.replace(/[_-]/g, "");
        case "residenceState":
        case "mailingState":
        case "landlordState":
        case "contactState":
          return val && val.length > 2 ? reverseStateMap[val] : val;
        default:
          return val;
      }
    })
    .value();

  // if unitSize is absent, set it based on the other unitSize fields
  if (!resultObj.unitSize) {
    const unitSizeFields = Object.keys(resultObj).filter((key) => key !== "unitSize" && key.startsWith("unitSize"));
    const unitSizeValue = unitSizeFields.reduce((acc: string[], key: string) => {
      if (resultObj[key]) {
        acc.push(key.replace("unitSize", "").toLowerCase());
      }

      return acc;
    }, []).join(",");

    resultObj.unitSize = unitSizeValue;
  }

  return resultObj;
};
